import React from "react";

const GlovoModal = ({
  title = "Glovo API Response",
  data = null,
  loading = false,
  error = null,
  modalId = "glovoModal",
  onClose,
  size = "modal-xl",
  showJson = true,
  children,
}) => {
  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3 text-muted">Chargement des données Glovo...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="alert alert-danger" role="alert">
          <h6 className="alert-heading">Erreur</h6>
          <p className="mb-0">{typeof error === 'string' ? error : JSON.stringify(error, null, 2)}</p>
        </div>
      );
    }

    if (children) {
      return children;
    }

    if (data && showJson) {
      return (
        <div className="glovo-response-container">
          <pre className="bg-light p-3 rounded border" style={{ 
            maxHeight: '500px', 
            overflow: 'auto',
            fontSize: '0.85rem'
          }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      );
    }

    if (data) {
      return renderStructuredData(data);
    }

    return (
      <div className="alert alert-info" role="alert">
        Aucune donnée disponible.
      </div>
    );
  };

  const renderStructuredData = (apiData) => {
    // Handle different response types
    if (apiData.reviews) {
      return renderReviews(apiData.reviews, apiData.summary);
    }
    if (apiData.categories) {
      return renderCategories(apiData.categories, apiData.summary);
    }
    if (apiData.products) {
      return renderProducts(apiData.products, apiData.summary || apiData.pagination);
    }
    if (apiData.orders) {
      return renderOrders(apiData.orders, apiData.summary);
    }
    if (apiData.order) {
      return renderOrderDetails(apiData.order);
    }
    if (apiData.portal) {
      return renderPortalInfo(apiData.portal, apiData.plateform);
    }
    if (apiData.availabilities) {
      return renderAvailabilities(apiData.availabilities);
    }

    // Default: show as JSON
    return (
      <pre className="bg-light p-3 rounded border" style={{ 
        maxHeight: '500px', 
        overflow: 'auto',
        fontSize: '0.85rem'
      }}>
        {JSON.stringify(apiData, null, 2)}
      </pre>
    );
  };

  const renderReviews = (reviews, summary) => (
    <div>
      {summary && (
        <div className="alert alert-info mb-3">
          <strong>Total:</strong> {summary.fetched || reviews.length} avis
          {summary.pages && <span> • <strong>Pages:</strong> {summary.pages}</span>}
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>ID</th>
              <th>Commande</th>
              <th>Note</th>
              <th>Commentaire</th>
              <th>Client</th>
              <th>Date</th>
              <th>Réponse</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review, index) => (
              <tr key={review.id || index}>
                <td><small className="text-muted">{review.id}</small></td>
                <td><small className="text-muted">{review.orderId}</small></td>
                <td>
                  <span className="badge bg-warning text-dark">
                    {'⭐'.repeat(review.rating || 0)}
                  </span>
                </td>
                <td>{review.comment || '—'}</td>
                <td>{review.customerName || '—'}</td>
                <td><small>{review.createdAt ? new Date(review.createdAt).toLocaleString() : '—'}</small></td>
                <td>
                  {review.reply ? (
                    <span className="badge bg-success">Répondu</span>
                  ) : (
                    <span className="badge bg-secondary">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCategories = (categories, summary) => (
    <div>
      {summary && (
        <div className="alert alert-info mb-3">
          <strong>Total:</strong> {summary.fetched || categories.length} catégories
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>ID</th>
              <th>Nom</th>
              <th>Description</th>
              <th>Position</th>
              <th>Parent</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat, index) => (
              <tr key={cat.id || index}>
                <td><small className="text-muted">{cat.id}</small></td>
                <td><strong>{cat.name}</strong></td>
                <td>{cat.description || '—'}</td>
                <td>{cat.position ?? '—'}</td>
                <td><small className="text-muted">{cat.parentId || '—'}</small></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderProducts = (products, metadata) => (
    <div>
      {metadata && (
        <div className="alert alert-info mb-3">
          {metadata.fetched && <span><strong>Total:</strong> {metadata.fetched} produits</span>}
          {metadata.totalCount && <span><strong>Total:</strong> {metadata.totalCount} produits</span>}
          {metadata.pages && <span> • <strong>Pages:</strong> {metadata.pages}</span>}
          {metadata.hasNext !== undefined && <span> • <strong>Suite:</strong> {metadata.hasNext ? 'Oui' : 'Non'}</span>}
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>SKU</th>
              <th>Nom</th>
              <th>Prix</th>
              <th>Actif</th>
              <th>Catégorie</th>
              <th>Code-barres</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => (
              <tr key={product.sku || index}>
                <td><small className="text-muted">{product.sku}</small></td>
                <td>
                  <div className="d-flex align-items-center">
                    {product.firstImage && (
                      <img 
                        src={product.firstImage} 
                        alt={product.name} 
                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                        className="me-2 rounded"
                      />
                    )}
                    <strong>{product.name}</strong>
                  </div>
                </td>
                <td>
                  {product.price !== undefined && (
                    <span className="badge bg-primary">{product.price.toFixed(2)}</span>
                  )}
                </td>
                <td>
                  <span className={`badge ${product.active ? 'bg-success' : 'bg-secondary'}`}>
                    {product.active ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td>
                  <small>{product.categoryName || product.categoryNames?.[0] || '—'}</small>
                </td>
                <td>
                  <small className="text-muted">
                    {product.barcode || product.pieceBarcodes?.[0] || '—'}
                  </small>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderOrders = (orders, summary) => (
    <div>
      {summary && (
        <div className="alert alert-info mb-3">
          <strong>Total:</strong> {summary.fetched || orders.length} commandes
          {summary.startDate && <span> • <strong>Du:</strong> {new Date(summary.startDate).toLocaleDateString()}</span>}
          {summary.endDate && <span> • <strong>Au:</strong> {new Date(summary.endDate).toLocaleDateString()}</span>}
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>ID Commande</th>
              <th>Statut</th>
              <th>Date</th>
              <th>Total</th>
              <th>Devise</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => (
              <tr key={order.orderId || index}>
                <td><small className="text-muted">{order.orderId}</small></td>
                <td>
                  <span className={`badge ${getOrderStatusClass(order.status)}`}>
                    {order.status}
                  </span>
                </td>
                <td><small>{order.createdAt ? new Date(order.createdAt).toLocaleString() : '—'}</small></td>
                <td><strong>{order.totalPrice?.toFixed(2) || '—'}</strong></td>
                <td>{order.currency || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderOrderDetails = (order) => (
    <div className="order-details">
      <div className="row mb-3">
        <div className="col-md-6">
          <h6>Informations Commande</h6>
          <p><strong>ID:</strong> {order.orderId}</p>
          <p><strong>Statut:</strong> <span className={`badge ${getOrderStatusClass(order.status)}`}>{order.status}</span></p>
          <p><strong>Date:</strong> {order.createdAt ? new Date(order.createdAt).toLocaleString() : '—'}</p>
          <p><strong>Total:</strong> {order.totalPrice?.toFixed(2)} {order.currency}</p>
        </div>
        <div className="col-md-6">
          <h6>Client</h6>
          {order.customer && (
            <>
              <p><strong>Nom:</strong> {order.customer.name || '—'}</p>
              <p><strong>Téléphone:</strong> {order.customer.phone || '—'}</p>
            </>
          )}
        </div>
      </div>
      
      {order.items && (
        <div className="mb-3">
          <h6>Articles</h6>
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Quantité</th>
                  <th>Prix</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.quantity}</td>
                    <td>{item.price?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {order.deliveryAddress && (
        <div>
          <h6>Adresse de Livraison</h6>
          <p>
            {order.deliveryAddress.street}<br />
            {order.deliveryAddress.city} {order.deliveryAddress.postalCode}
          </p>
        </div>
      )}
    </div>
  );

  const renderPortalInfo = (portal, plateform) => (
    <div>
      <div className="alert alert-success mb-3">
        <h6 className="alert-heading">Connexion Portail Réussie</h6>
        {portal.vendorName && <p><strong>Vendeur:</strong> {portal.vendorName}</p>}
        {portal.country && <p><strong>Pays:</strong> {portal.country}</p>}
        {portal.expiresAtUtc && (
          <p><strong>Expire:</strong> {new Date(portal.expiresAtUtc).toLocaleString()}</p>
        )}
      </div>
      
      {plateform && (
        <div className="card mb-3">
          <div className="card-body">
            <h6>Plateforme</h6>
            <p><strong>Type:</strong> {plateform.type}</p>
            <p><strong>Login:</strong> {plateform.login}</p>
            <p><strong>Connecté:</strong> {plateform.is_connected ? 'Oui' : 'Non'}</p>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h6>Détails Session</h6>
          <pre className="bg-light p-2 rounded small mb-0">
            {JSON.stringify(portal, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );

  const renderAvailabilities = (availabilities) => (
    <div className="table-responsive">
      <table className="table table-striped table-hover">
        <thead className="table-dark">
          <tr>
            <th>Restaurant</th>
            <th>État</th>
            <th>Modifiable</th>
            <th>Prochaine ouverture</th>
            <th>Entity</th>
          </tr>
        </thead>
        <tbody>
          {availabilities.map((item, index) => (
            <tr key={item.platformRestaurantId || index}>
              <td>
                <strong>{item.name || item.platformRestaurantId}</strong>
                {item.platformKey && <><br /><small className="text-muted">{item.platformKey}</small></>}
              </td>
              <td>
                <span className={`badge ${getAvailabilityClass(item.availabilityState)}`}>
                  {formatAvailabilityLabel(item.availabilityState)}
                </span>
              </td>
              <td>{item.changeable ? 'Oui' : 'Non'}</td>
              <td>
                <small>
                  {item.closedUntil
                    ? new Date(item.closedUntil).toLocaleString()
                    : item.currentSlotEndAt
                    ? new Date(item.currentSlotEndAt).toLocaleString()
                    : '—'}
                </small>
              </td>
              <td><small className="text-muted">{item.globalEntityId || '—'}</small></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const getOrderStatusClass = (status) => {
    const statusMap = {
      PENDING: 'bg-warning',
      CONFIRMED: 'bg-info',
      PREPARING: 'bg-primary',
      READY: 'bg-success',
      DELIVERED: 'bg-success',
      CANCELLED: 'bg-danger',
    };
    return statusMap[status] || 'bg-secondary';
  };

  const getAvailabilityClass = (state) => {
    if (!state) return 'bg-secondary';
    const stateStr = state.toString().toLowerCase();
    if (stateStr.includes('open') || stateStr.includes('available')) return 'bg-success';
    if (stateStr.includes('closed')) return 'bg-danger';
    return 'bg-warning';
  };

  const formatAvailabilityLabel = (state) => {
    if (!state) return 'Inconnue';
    return state
      .toString()
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <div className="modal fade" id={modalId} tabIndex="-1" aria-hidden="true">
      <div className={`modal-dialog ${size}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={handleClose}
            ></button>
          </div>
          <div className="modal-body">
            {renderContent()}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              data-bs-dismiss="modal"
              onClick={handleClose}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlovoModal;
